<template>
    <section class="tw:space-y-4">
        <!-- Header compacto, consistente con otros tabs -->
        <div class="tw:flex tw:items-center tw:justify-between tw:px-6">
            <div>
                <h2 class="tw:text-sm tw:font-semibold tw:text-gray-500 tw:uppercase tw:tracking-wide">
                    Historial de cesiones
                </h2>
                <p class="tw:text-xs tw:font-semibold tw:text-gray-400">
                    Seguimiento de transferencias de asiento del abonado
                </p>
            </div>
        </div>

        <!-- BaseTable con skeleton, empty y datos -->
        <BaseTable
            :naked="true"
            :loading="resolvedLoading"
            :skeleton="resolvedLoading"
            :data="resolvedHistorial"
            :columns="tableColumns"
            empty-text="No hay transferencias registradas para este abonado."
            row-key="id"
            :default-col-width="defaultColWidth"
        >
            <template #cell-accion="{ row }">
                <span :class="getActionBadgeClass(row.accion)">
                    {{ formatAccion(row.accion) }}
                </span>
            </template>
            <template #cell-fila="{ row }">
                <span class="tw:text-sm tw:font-medium tw:text-slate-700">{{ row.fila || '—' }}</span>
            </template>
            <template #cell-asiento="{ row }">
                <span class="tw:text-sm tw:font-medium tw:text-slate-700">{{ row.asiento || '—' }}</span>
            </template>
            <template #cell-fecha="{ row }">
                <span class="tw:text-sm tw:text-slate-500">{{ formatDate(row.fecha_accion) }}</span>
            </template>
            <template #cell-detalle="{ row }">
                <span class="tw:text-sm tw:text-slate-500">{{ row.detalle || '—' }}</span>
            </template>
        </BaseTable>

        <!-- Paginación -->
        <TableFooter
            v-if="hasResults && !resolvedError"
            :value="resolvedFilters"
            :meta="resolvedMeta"
            @input="handleFooterInput"
        />
    </section>
</template>

<script setup>
    import { computed, isRef, toRef } from 'vue'
    import { useAdminCesionHistorial } from '@/composables/admin/abonados/useAdminCesionHistorial'
    import BaseTable from '@/modules/admin/ui/table/BaseTable.vue'
    import TableFooter from '@/modules/admin/ui/table/TableFooter.vue'
    import {
        formatCesionAccion,
        formatCesionDate,
        getCesionActionBadgeClass,
    } from '@/modules/admin/abonados/components/cesionHistorialPresenter'

    const props = defineProps({
        abonadoId: {
            type: [String, Number],
            required: true
        },
        defaultColWidth: {
            type: String,
            default: '120px'
        }
    })

    const abonadoId = toRef(props, 'abonadoId')

    const historialState = useAdminCesionHistorial(abonadoId)

    const {
        historial,
        loading,
        error,
        filters,
        meta,
        loadHistorial,
        updateFilters,
    } = historialState

    const normalizeMaybeRef = value => (isRef(value) ? value : computed(() => value))

    const resolvedHistorial = normalizeMaybeRef(historial ?? historialState.historial ?? [])
    const resolvedLoading = normalizeMaybeRef(loading ?? historialState.loading ?? false)
    const resolvedError = normalizeMaybeRef(error ?? historialState.error ?? null)
    const resolvedFilters = normalizeMaybeRef(filters ?? historialState.filters ?? {
        page: historialState.currentPage ?? 1,
        per_page: historialState.perPage ?? 10,
    })
    const resolvedMeta = normalizeMaybeRef(meta ?? historialState.meta ?? {
        total: historialState.total ?? historialState.totalRows ?? 0,
        current_page: historialState.currentPage ?? 1,
        per_page: historialState.perPage ?? 10,
        last_page: 1,
    })

    const tableColumns = [
        { key: 'accion', label: 'Acción' },
        { key: 'fila', label: 'Fila', class: 'tw:text-center' },
        { key: 'asiento', label: 'Asiento', class: 'tw:text-center' },
        { key: 'fecha', label: 'Fecha', field: 'fecha_accion', class: 'tw:text-center' },
        { key: 'detalle', label: 'Detalle' },
    ]

    const formatAccion = historialState.formatAccion || formatCesionAccion
    const getActionBadgeClass = historialState.getActionBadgeClass || getCesionActionBadgeClass
    const formatDate = historialState.formatDate || formatCesionDate

    const total = computed(() => resolvedMeta.value.total || 0)
    const hasResults = computed(() => total.value > 0)

    function handleFooterInput(nextFilters = {}) {
        updateFilters(nextFilters)
    }
</script>
